import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BadgeCheck, ChevronRight, MapPin, Star } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { SERVICE_CATALOG } from '@/lib/domain';
import { Avatar, EmptyState, PageHeader } from '@/components/ui';
import Spinner from '@/components/Spinner';
import styles from './ProvidersList.module.css';

type Provider = {
  id: string;
  full_name: string;
  city: string;
  rating: number;
  description: string;
  avatar_url: string | null;
};

export default function ProvidersList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const serviceName = searchParams.get('service') ?? '';
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);

    void (async () => {
      try {
        let query = supabase
          .from('providers')
          .select('id, rating, description, profiles(full_name, city, avatar_url)')
          .eq('is_approved', true);

        if (serviceName) {
          const { data: serviceRow } = await supabase
            .from('services')
            .select('id')
            .eq('name', serviceName)
            .maybeSingle();

          if (!serviceRow) {
            if (active) {
              setProviders([]);
              setLoading(false);
            }
            return;
          }

          const { data: providerIds } = await supabase
            .from('provider_services')
            .select('provider_id')
            .eq('service_id', serviceRow.id);

          if (!providerIds || providerIds.length === 0) {
            if (active) {
              setProviders([]);
              setLoading(false);
            }
            return;
          }

          query = query.in(
            'id',
            providerIds.map((row: any) => row.provider_id)
          );
        }

        const { data, error } = await query;
        if (!active) return;

        if (!error && data) {
          setProviders(
            data.map((row: any) => ({
              id: row.id,
              full_name: row.profiles?.full_name ?? 'Prestataire',
              city: row.profiles?.city ?? '',
              rating: Number(row.rating ?? 0),
              description: row.description ?? '',
              avatar_url: row.profiles?.avatar_url ?? null,
            }))
          );
        }
      } catch (err) {
        console.error('Error loading providers:', err);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [serviceName]);

  const selectService = (name: string) => {
    if (name) setSearchParams({ service: name });
    else setSearchParams({});
  };

  return (
    <div>
      <PageHeader title={serviceName || 'Prestataires'} back="/app" />

      <div className={styles.body}>
        <div className={styles.filters}>
          <button
            type="button"
            className={serviceName === '' ? 'chip chip--on' : 'chip'}
            onClick={() => selectService('')}
            aria-pressed={serviceName === ''}
          >
            Tous
          </button>
          {SERVICE_CATALOG.map((service) => (
            <button
              key={service.id}
              type="button"
              className={serviceName === service.name ? 'chip chip--on' : 'chip'}
              onClick={() => selectService(service.name)}
              aria-pressed={serviceName === service.name}
            >
              {service.name}
            </button>
          ))}
        </div>

        {loading ? (
          <Spinner block />
        ) : providers.length === 0 ? (
          <EmptyState
            illustration="/assets/illustrations/search.png"
            title="Aucun prestataire disponible"
            text={
              serviceName
                ? `Aucun prestataire ne propose « ${serviceName} » pour le moment.`
                : 'Revenez plus tard pour découvrir de nouveaux prestataires.'
            }
          />
        ) : (
          <ul className={styles.list}>
            {providers.map((provider) => (
              <li key={provider.id}>
                <Link className={styles.card} to={`/app/providers/${provider.id}`}>
                  <Avatar name={provider.full_name} src={provider.avatar_url} size={58} />
                  <div className={styles.info}>
                    <p className={styles.name}>
                      {provider.full_name}
                      <BadgeCheck size={16} className={styles.verified} aria-label="Vérifié" />
                    </p>
                    {provider.description ? (
                      <p className={styles.description}>{provider.description}</p>
                    ) : null}
                    <p className={styles.metaRow}>
                      {provider.rating > 0 ? (
                        <span className={styles.rating}>
                          <Star size={13} color="#FFB21A" fill="#FFB21A" />
                          {provider.rating.toFixed(1)}
                        </span>
                      ) : null}
                      {provider.city ? (
                        <span className={styles.city}>
                          <MapPin size={13} /> {provider.city}
                        </span>
                      ) : null}
                    </p>
                  </div>
                  <span className={styles.go} aria-hidden="true">
                    <ChevronRight size={18} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
